import type { Document } from './dueDiligence';
export const RECORDING_MAX_BYTES = 2 * 1024 * 1024 * 1024;
export const RECORDING_CHUNK_BYTES = 8 * 1024 * 1024;
export type RecordingUpload = {
  id: string;
  name: string;
  mediaType: string;
  totalSize: number;
  sampleFingerprint: string;
  offset: number;
  chunkSize: number;
  status: 'active' | 'completed';
  expiresAt: string;
  chunkHashes: { offset: number; sha256: string }[];
  document: Document | null;
};
export type TransferProgress = {
  upload: RecordingUpload;
  phase: 'checking' | 'uploading' | 'finalizing';
};
export type RecordingApi = (
  path: string,
  init?: RequestInit,
) => Promise<unknown>;
export function recordingMediaType(file: Pick<File, 'name' | 'type'>): string {
  if (
    [
      'audio/mpeg',
      'audio/wav',
      'audio/x-wav',
      'audio/flac',
      'audio/ogg',
      'audio/webm',
      'audio/mp4',
      'audio/x-m4a',
      'video/mp4',
      'video/quicktime',
      'video/webm',
    ].includes(file.type)
  )
    return file.type;
  const type: Record<string, string> = {
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    flac: 'audio/flac',
    ogg: 'audio/ogg',
    m4a: 'audio/mp4',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    webm: 'video/webm',
  };
  return type[file.name.split('.').at(-1)?.toLowerCase() ?? ''] ?? '';
}
async function digest(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(
    await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()),
  );
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join(
    '',
  );
}
// Fast selection check followed by every previously accepted chunk's hash.
// The server separately hashes the full original when committing the document.
export async function recordingFingerprint(file: File): Promise<string> {
  return digest(
    new Blob([
      JSON.stringify({
        name: file.name,
        size: file.size,
        mediaType: recordingMediaType(file),
      }),
      '\n',
      file.slice(0, 1024 * 1024),
      file.slice(Math.max(0, file.size - 1024 * 1024)),
    ]),
  );
}
function checked(
  value: unknown,
  file: File,
  fingerprint: string,
): RecordingUpload {
  const upload = value as RecordingUpload;
  if (
    !upload ||
    upload.name !== file.name ||
    upload.mediaType !== recordingMediaType(file) ||
    upload.totalSize !== file.size ||
    upload.sampleFingerprint !== fingerprint ||
    upload.chunkSize !== RECORDING_CHUNK_BYTES ||
    !Number.isSafeInteger(upload.offset) ||
    upload.offset < 0 ||
    upload.offset > file.size ||
    (upload.offset !== file.size && upload.offset % upload.chunkSize !== 0) ||
    !Array.isArray(upload.chunkHashes) ||
    !['active', 'completed'].includes(upload.status)
  )
    throw new Error(
      'Bitte dieselbe Originaldatei wie beim begonnenen Upload auswählen.',
    );
  return upload;
}
function aborted(signal: AbortSignal) {
  signal.throwIfAborted();
}
export async function transferRecording(
  file: File,
  options: {
    base: string;
    api: RecordingApi;
    signal: AbortSignal;
    resume?: RecordingUpload;
    onProgress: (progress: TransferProgress) => void;
  },
): Promise<Document> {
  const { base, api, signal, onProgress } = options;
  if (
    !recordingMediaType(file) ||
    !file.size ||
    file.size > RECORDING_MAX_BYTES
  )
    throw new Error(
      'Unterstützte Audio- oder Videoaufnahme bis 2 GB auswählen.',
    );
  const fingerprint = await recordingFingerprint(file);
  aborted(signal);
  let upload: RecordingUpload;
  if (options.resume) {
    upload = checked(
      await api(`${base}/${encodeURIComponent(options.resume.id)}`, { signal }),
      file,
      fingerprint,
    );
  } else {
    const id = crypto.randomUUID();
    const input = {
      id,
      name: file.name,
      mediaType: recordingMediaType(file),
      totalSize: file.size,
      sampleFingerprint: fingerprint,
    };
    try {
      upload = checked(
        await api(base, {
          method: 'POST',
          signal,
          body: JSON.stringify(input),
        }),
        file,
        fingerprint,
      );
    } catch (error) {
      aborted(signal);
      try {
        upload = checked(
          await api(`${base}/${id}`, { signal }),
          file,
          fingerprint,
        );
      } catch {
        throw error;
      }
    }
  }
  onProgress({ upload, phase: 'checking' });
  if (upload.chunkHashes.length !== Math.ceil(upload.offset / upload.chunkSize))
    throw new Error(
      'Uploadstand ist unvollständig. Neu laden und erneut prüfen.',
    );
  for (let index = 0; index < upload.chunkHashes.length; index++) {
    aborted(signal);
    const chunk = upload.chunkHashes[index];
    const start = index * upload.chunkSize;
    if (
      chunk.offset !== start ||
      (await digest(
        file.slice(start, Math.min(start + upload.chunkSize, file.size)),
      )) !== chunk.sha256
    )
      throw new Error(
        'Ein bereits übertragener Teil passt nicht zur ausgewählten Datei. Die Originaldatei auswählen oder den Upload verwerfen.',
      );
  }
  const endpoint = `${base}/${encodeURIComponent(upload.id)}`;
  while (upload.status === 'active' && upload.offset < file.size) {
    aborted(signal);
    onProgress({ upload, phase: 'uploading' });
    const start = upload.offset;
    const end = Math.min(file.size, start + upload.chunkSize);
    const body = file.slice(start, end);
    try {
      upload = checked(
        await api(`${endpoint}/chunks?offset=${start}`, {
          method: 'PUT',
          signal,
          headers: { 'Content-Type': 'application/octet-stream' },
          body,
        }),
        file,
        fingerprint,
      );
    } catch (error) {
      aborted(signal);
      try {
        const current = checked(
          await api(endpoint, { signal }),
          file,
          fingerprint,
        );
        if (current.offset !== end) throw error;
        upload = current;
      } catch {
        throw error;
      }
    }
    if (upload.offset !== end)
      throw new Error(
        'Uploadstand wurde parallel geändert. Pausieren und dieselbe Datei erneut auswählen.',
      );
    onProgress({ upload, phase: 'uploading' });
  }
  aborted(signal);
  onProgress({ upload, phase: 'finalizing' });
  if (upload.status !== 'completed') {
    try {
      upload = checked(
        await api(`${endpoint}/complete`, { method: 'POST', signal }),
        file,
        fingerprint,
      );
    } catch (error) {
      aborted(signal);
      try {
        upload = checked(await api(endpoint, { signal }), file, fingerprint);
        if (upload.status !== 'completed') throw error;
      } catch {
        throw error;
      }
    }
  }
  aborted(signal);
  if (
    upload.status !== 'completed' ||
    !upload.document ||
    upload.document.size !== file.size ||
    !/^[a-f0-9]{64}$/.test(upload.document.sha256)
  )
    throw new Error(
      'Aufnahme wurde noch nicht vollständig geprüft. Uploadstand neu laden.',
    );
  return upload.document;
}
